import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, ChevronRight, Search } from "lucide-react";

interface Location {
  id: string;
  name: string;
  address: string;
  formatted_address: string;
  location_city?: string;
  location_short_address?: string;
  primary_phone?: string;
  website_uri?: string;
  latitude?: number;
  longitude?: number;
  regular_hours?: any;
  auto_detected_services?: string[];
  photos?: any[];
  rating?: number;
  review_count?: number;
  primary_category?: string;
  additional_categories?: any[];
  description?: string;
  distance?: number;
  media_items?: any;
}

interface StoreLocatorTreeViewProps {
  locations: Location[];
  locationsByCity: { [city: string]: Location[] };
  selectedLocation: Location | null;
  selectedCity: string;
  expandedCities: Set<string>;
  allCities: string[];
  theme: any;
  subdomain: string;
  onLocationSelect: (location: Location) => void;
  onCityChange: (city: string) => void;
  onCityToggle: (city: string) => void;
  formatAddress: (location: Location) => string;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  showSearchBar?: boolean;
}

const StoreLocatorTreeView = ({
  locations,
  locationsByCity,
  selectedLocation,
  selectedCity,
  expandedCities,
  allCities,
  theme,
  subdomain,
  onLocationSelect,
  onCityChange,
  onCityToggle,
  formatAddress,
  onSearch,
  searchQuery = "",
  showSearchBar = false,
}: StoreLocatorTreeViewProps) => {
  return (
      <Card className="h-full p-3 md:p-4 flex flex-col overflow-hidden">
        <div className="mb-3 md:mb-4 space-y-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <h3 className="font-semibold text-base md:text-lg">All Locations</h3>
          </div>

          {showSearchBar && onSearch && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search city, state, or ZIP"
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                className="pl-10 w-full text-sm"
              />
            </div>
          )}

          <Select value={selectedCity} onValueChange={onCityChange}>
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder="Filter by city" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {allCities.map(city => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
          {Object.entries(locationsByCity).map(([city, cityLocations]: [string, any]) => (
            <div key={city} className="border-b border-gray-100 pb-2">
              <button
                onClick={() => onCityToggle(city)}
                className="flex items-center justify-between w-full text-left py-2 hover:bg-gray-50 rounded px-2"
              >
                <span className="font-medium text-sm">{city}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{cityLocations.length}</span>
                  <ChevronRight
                    className={`h-4 w-4 transition-transform ${
                      expandedCities.has(city) ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </button>
              {expandedCities.has(city) && (
                <div className="ml-2 md:ml-4 space-y-1">
                  {cityLocations.map((location: Location) => (
                    <button
                      key={location.id}
                      onClick={() => onLocationSelect(location)}
                      className={`w-full text-left p-2 rounded text-sm hover:bg-gray-100 transition-colors ${
                        selectedLocation?.id === location.id
                          ? 'bg-blue-50 border-l-2'
                          : ''
                      }`}
                      style={{
                        borderLeftColor: selectedLocation?.id === location.id
                          ? theme?.primaryColor
                          : undefined,
                      }}
                    >
                      <div className="font-medium text-sm">{location.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {location.location_short_address || formatAddress(location).split(',')[0]}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
  );
};

export default StoreLocatorTreeView;
